// Som suave de mensagem nova, gerado pela Web Audio API (sem arquivo de áudio nem dependência).
// O navegador só libera áudio depois de uma interação do usuário na página; o clique do login
// já conta. Se ainda estiver bloqueado, falha em silêncio e o badge continua avisando.

const MUTE_KEY = "c4uChatMuted";

let context: AudioContext | null = null;

const getContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!context) context = new Ctor();
  return context;
};

// Destrava o áudio no primeiro gesto do usuário na página (clique, toque ou tecla): sem isso o
// contexto nasce "suspended" e o som de mensagem nova falha em silêncio até alguém interagir.
let armed = false;
export const armAudioOnFirstGesture = (): void => {
  if (armed || typeof window === "undefined") return;
  armed = true;
  const unlock = () => {
    const ctx = getContext();
    if (ctx && ctx.state === "suspended") {
      ctx.resume().catch(() => undefined);
    }
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("touchstart", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);
  window.addEventListener("touchstart", unlock);
};

export const isChatMuted = (): boolean => {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
};

export const setChatMuted = (muted: boolean): void => {
  try {
    localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
  } catch {
    // preferência não persistida — segue só na sessão
  }
};

// Duas notas curtas (Mi5 → Sol5) com envelope suave: discreto, sem lembrar alarme.
export const playIncomingTone = async (): Promise<void> => {
  const ctx = getContext();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") await ctx.resume();
  } catch {
    return;
  }
  if (ctx.state !== "running") return;

  const now = ctx.currentTime;
  const notes: [number, number][] = [[659.25, 0], [783.99, 0.14]];
  for (const [freq, offset] of notes) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, now + offset);
    gain.gain.linearRampToValueAtTime(0.28, now + offset + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.38);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + offset);
    osc.stop(now + offset + 0.4);
  }
};

// Aviso do sistema operacional quando chega mensagem com a aba em segundo plano (RN-551f): quem
// está em outra aba ou com o navegador minimizado não vê o badge nem sempre ouve o som. A permissão
// é pedida no clique que abre o chat (o navegador só aceita o pedido dentro de um gesto).
export const requestDesktopNotifications = (): void => {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission === "default") {
    Notification.requestPermission().catch(() => undefined);
  }
};

export const showDesktopNotification = (body: string): void => {
  if (typeof document === "undefined" || !document.hidden) return;
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  try {
    const notice = new Notification("Clinic4Us", { body, tag: "c4u-chat", icon: "/favicon.ico" });
    notice.onclick = () => {
      window.focus();
      notice.close();
    };
  } catch {
    // navegador que só aceita notificação via service worker (ex.: Chrome no Android): fica o badge
  }
};
